/**
 * Data access for the public site.
 *
 * Every public page needs the same three things from a request — the payload,
 * whether it is still in flight, and whether it failed — so the shape is fixed
 * once here as `{data, loading, error, reload}` and never re-invented per page.
 *
 * Responses are shared through a module-level cache keyed by resource. That is
 * not premature optimisation: the header, the footer and the home page all read
 * the site settings, and without a shared cache one navigation would fire three
 * identical requests and paint three separate skeletons. The cache also means
 * moving news → detail → back does not re-fetch a list the visitor just read.
 *
 * Layer rule: logic only. No JSX, no class names, no styling decision. A page
 * decides how a loading state *looks*; this module only decides that there is
 * one.
 *
 * 公开站点的数据访问。
 *
 * 每个公开页面对一次请求都只关心三件事——数据、是否仍在请求中、是否失败——因此
 * 形状在这里定死一次为 {data, loading, error, reload}，不在各页面重复发明。
 *
 * 响应通过模块级缓存按资源键共享。这不是过早优化：页头、页脚和首页都要读站点设置，
 * 没有共享缓存的话，一次导航会发出三个完全相同的请求、画出三块各自的骨架屏。
 * 缓存同时意味着「新闻列表 → 详情 → 返回」不会重新拉一遍访客刚读过的列表。
 *
 * 分层规则：只有逻辑。不含 JSX、不含类名、不做样式决策。加载态「长什么样」由页面
 * 决定，本模块只决定「存在一个加载态」。
 */

import { useCallback, useEffect, useReducer } from 'react'

import { contentApi, siteApi } from '../lib/api.js'

/**
 * Settled and in-flight responses, keyed by resource.
 *
 * Entry shape: `{data, error, loading, promise}`. `data` and `error` are
 * mutually exclusive — a reload that fails clears the stale payload rather than
 * showing yesterday's list under an error banner.
 *
 * 已完成与进行中的响应，按资源键存放。条目形状为 {data, error, loading, promise}。
 * data 与 error 互斥——重试失败会清掉旧数据，而不是在错误提示下继续显示昨天的列表。
 */
const CACHE = new Map()

/** Re-render callbacks per resource key. 每个资源键上的重渲染回调。 */
const WATCHERS = new Map()

/** Read-only stand-in for a resource nobody has requested yet. 尚未请求过的资源的只读占位。 */
const PENDING = Object.freeze({ data: null, error: null, loading: true, promise: null })

/**
 * Get (creating if needed) the cache entry for a key.
 *
 * 取出（必要时创建）某个键的缓存条目。
 *
 * @param {string} key Resource key / 资源键。
 * @returns {{data: any, error: Error|null, loading: boolean, promise: Promise|null}} Entry / 条目。
 */
function entryFor(key) {
  let entry = CACHE.get(key)
  if (!entry) {
    entry = { data: null, error: null, loading: false, promise: null }
    CACHE.set(key, entry)
  }
  return entry
}

/**
 * Tell every mounted hook watching this key that its entry changed.
 *
 * 通知所有正在观察该键的 hook：条目已变化。
 *
 * @param {string} key Resource key / 资源键。
 * @returns {void}
 */
function announce(key) {
  const watchers = WATCHERS.get(key)
  if (watchers) watchers.forEach((notify) => notify())
}

/**
 * Subscribe to a key, returning the unsubscribe function.
 *
 * 订阅某个键，返回退订函数。
 *
 * @param {string} key Resource key / 资源键。
 * @param {() => void} notify Re-render callback / 重渲染回调。
 * @returns {() => void} Unsubscribe / 退订函数。
 */
function watch(key, notify) {
  if (!WATCHERS.has(key)) WATCHERS.set(key, new Set())
  WATCHERS.get(key).add(notify)
  return () => {
    const watchers = WATCHERS.get(key)
    if (!watchers) return
    watchers.delete(notify)
    if (!watchers.size) WATCHERS.delete(key)
  }
}

/**
 * Fetch a resource into the cache, de-duplicating concurrent callers.
 *
 * The rejection is caught and stored rather than propagated: three components
 * awaiting the same failed request would otherwise produce three unhandled
 * rejections for one server error.
 *
 * 把资源取进缓存，并合并并发调用。这里捕获并存下异常而不是向外抛：否则三个组件
 * 等待同一个失败请求，会为同一个服务端错误产生三个未处理的 rejection。
 *
 * @param {string} key Resource key / 资源键。
 * @param {() => Promise<any>} fetcher Request to run / 要执行的请求。
 * @param {boolean} [force] Re-fetch even when already settled / 即使已有结果也重新请求。
 * @returns {Promise<any>} Resolves with the payload, or null on failure / 成功返回数据，失败返回 null。
 */
function load(key, fetcher, force = false) {
  const entry = entryFor(key)
  // An in-flight request already satisfies a forced reload — firing a second
  // one would race the first and could resolve out of order.
  // 正在进行的请求已经能满足强制刷新——再发一个会和前一个赛跑，可能乱序返回。
  if (entry.promise) return entry.promise
  if (!force && (entry.data !== null || entry.error !== null)) return Promise.resolve(entry.data)

  entry.loading = true
  entry.error = null
  announce(key)

  entry.promise = Promise.resolve()
    .then(fetcher)
    .then((data) => {
      entry.data = data
      entry.error = null
    })
    .catch((error) => {
      entry.data = null
      entry.error = error
    })
    .then(() => {
      entry.loading = false
      entry.promise = null
      announce(key)
      return entry.data
    })

  return entry.promise
}

/**
 * Subscribe a component to one cached resource.
 *
 * `fetcher` must be referentially stable (build it with `useCallback`), because
 * it is part of the effect's dependency list — an inline arrow would re-run the
 * request on every render.
 *
 * 让组件订阅一个缓存资源。fetcher 必须引用稳定（用 useCallback 构造），因为它是
 * effect 依赖项之一——写成内联箭头函数会导致每次渲染都重新请求。
 *
 * @param {string} key Resource key / 资源键。
 * @param {() => Promise<any>} fetcher Stable request function / 引用稳定的请求函数。
 * @param {boolean} [enabled] Skip entirely when false / 为 false 时完全跳过。
 * @returns {{data: any, loading: boolean, error: Error|null, reload: () => void}} Resource state / 资源状态。
 */
function useResource(key, fetcher, enabled = true) {
  const [, rerender] = useReducer((tick) => tick + 1, 0)

  useEffect(() => {
    if (!enabled) return undefined
    const unwatch = watch(key, rerender)
    load(key, fetcher)
    return unwatch
  }, [key, fetcher, enabled])

  const reload = useCallback(() => {
    if (enabled) load(key, fetcher, true)
  }, [key, fetcher, enabled])

  // Read without creating: touching the Map during render would be a side
  // effect, and a key nobody has fetched yet is simply "still pending".
  // 只读不创建：渲染期间写 Map 属于副作用；没人取过的键就是「仍在等待」。
  const entry = enabled ? CACHE.get(key) || PENDING : PENDING

  return {
    data: entry.data,
    error: entry.error,
    // The first render happens before the effect fires, so an untouched entry
    // must already report loading — otherwise the page flashes its empty state.
    // A disabled resource is never loading: nothing was asked for, so nothing
    // is pending, and a page waiting on it would spin forever.
    // 首次渲染早于 effect 执行，因此未触碰过的条目必须已经处于 loading——
    // 否则页面会先闪一下空状态。被停用的资源永远不是 loading：什么都没请求，
    // 也就没有什么在等，否则等它的页面会一直转圈。
    loading: enabled && (entry.loading || (entry.data === null && entry.error === null)),
    reload,
  }
}

/**
 * Global site settings: group name, tagline, contact details, ICP line.
 *
 * 全局站点设置：课题组名称、一句话定位、联系方式、备案号。
 *
 * @returns {{data: object|null, loading: boolean, error: Error|null, reload: () => void}} Site state / 站点设置状态。
 */
export function useSite() {
  const fetcher = useCallback(() => siteApi.get(), [])
  return useResource('site', fetcher)
}

/**
 * Published records of one collection, in the order the schema declares.
 *
 * Resolves to `[]` rather than null on success, so a page can distinguish
 * "loaded and genuinely empty" from "not loaded yet" without a second flag.
 *
 * 某个集合的已发布记录，按 schema 声明的顺序返回。成功时解析为 []（而不是 null），
 * 这样页面不用第二个标志位就能区分「加载完确实是空的」和「还没加载」。
 *
 * @param {string} collection Collection key, e.g. 'news' / 集合键名。
 * @returns {{data: object[]|null, loading: boolean, error: Error|null, reload: () => void}} Collection state / 集合状态。
 */
export function useCollection(collection) {
  const fetcher = useCallback(async () => {
    const payload = await contentApi.list(collection)
    return Array.isArray(payload.items) ? payload.items : []
  }, [collection])
  return useResource(`collection:${collection}`, fetcher)
}

/**
 * One published record by id, for the two detail pages.
 *
 * A missing `id` disables the hook rather than requesting `/content/news/
 * undefined`, which the server would answer with a 404 the page then has to
 * explain away.
 *
 * 按 id 取单条已发布记录，供两个详情页使用。id 缺失时直接停用 hook，而不是去请求
 * /content/news/undefined——那会拿回一个 404，页面还得费劲解释它。
 *
 * @param {string} collection Collection key / 集合键名。
 * @param {string} [id] Record id / 记录 id。
 * @returns {{data: object|null, loading: boolean, error: Error|null, reload: () => void}} Record state / 记录状态。
 */
export function useRecord(collection, id) {
  const fetcher = useCallback(() => contentApi.get(collection, id), [collection, id])
  return useResource(`record:${collection}:${id}`, fetcher, Boolean(id))
}

/**
 * Resolve an upload field to a URL.
 *
 * An upload may be stored as the bare URL string or as the whole asset record
 * the upload endpoint returned (`{url, name, size, mime}`) — certificates, for
 * instance, keep the asset so the original filename survives. Rendering code
 * should not have to care which, and should never render the string
 * "[object Object]" into an `src` because it guessed wrong.
 *
 * 把上传字段解析成 URL。
 *
 * 上传值可能存成裸 URL 字符串，也可能存成上传接口返回的整条资源记录
 * （{url, name, size, mime}）——例如证书就保留整条记录，以便留住原始文件名。
 * 渲染代码不该关心是哪一种，更不该因为猜错而把 "[object Object]" 塞进 src。
 *
 * @param {string|{url?: string}|null|undefined} value Upload field value / 上传字段的值。
 * @returns {string} URL, or '' when absent / URL，缺失时为空串。
 */
export function assetUrl(value) {
  if (!value) return ''
  if (typeof value === 'string') return value
  return typeof value.url === 'string' ? value.url : ''
}

/**
 * The thumbnail address for an upload, derived from its own URL.
 *
 * A string operation rather than a lookup, because the places that need it hold a URL and
 * nothing else. The server answers this address for every upload — with the thumbnail
 * when there is one, with the original when there is not — so a caller never has to
 * handle a miss.
 *
 * Use it wherever a picture is displayed small: a library tile, a row thumbnail, a form
 * preview. Do not use it where the picture is the point — an article's figure, a
 * certificate being read — because a 480px image blown up is worse than a slow one.
 *
 * 某个上传文件的缩略图地址，由它自己的 URL 推导而来。
 *
 * 是字符串运算而不是查表，因为需要它的地方只拿得到一个 URL。服务端对每一个上传文件都
 * 会应答这个地址——有缩略图给缩略图，没有就给原图——因此调用方永远不必处理「找不到」。
 *
 * 凡是把图显示得很小的地方都该用它：媒体库磁贴、列表行缩略图、表单预览。
 * 而「图本身就是内容」的地方不要用——正文插图、正在被阅读的证书——
 * 因为把一张 480px 的图放大，比让它加载得慢更糟。
 *
 * @param {string|{url?: string}|null|undefined} value Upload field value / 上传字段的值。
 * @returns {string} Thumbnail URL, or '' when absent / 缩略图 URL，缺失时为空串。
 */
export function thumbUrl(value) {
  const url = assetUrl(value)
  if (!url) return ''
  const name = url.split('/').pop()
  return name && url.startsWith('/uploads/') ? `/uploads/thumb/${name}` : url
}

/**
 * Human-readable name of an upload, falling back to its last path segment.
 *
 * 上传文件的可读名称，缺失时回退为 URL 的最后一段。
 *
 * @param {string|{url?: string, name?: string}|null|undefined} value Upload field value / 上传字段的值。
 * @returns {string} File name / 文件名。
 */
export function assetName(value) {
  if (value && typeof value === 'object' && value.name) return String(value.name)
  const url = assetUrl(value)
  return url ? url.split('/').pop() : ''
}
